import { DataSource, DataSourceMode, EditRowDecorator, Layout, MultiButton, Snackbar } from "@mcleod/components";
import { ModelRow } from "@mcleod/core";
import { DriverEvent } from "./DriverEvent";
import { CommonDialogs } from "@mcleod/common";
import { getDispatchControlBoolean } from "@mcleod/general";

interface DriverEventProps {
    equipmentId: string;
    equipmentEditable?: boolean;
    equipmentName: string;
    driverMgrId: string;
    driverMgrName: string;
    equipStatusId?: string;
    doAfterSave?: () => void;
}

export class DriverEventSlideout extends DriverEvent {
    private driverEventLayout: DriverEvent;
    private preventClose: any;

    constructor() {
        super({
            maxWidth: 1100, width: window.innerWidth * .75, height: window.innerHeight,
            backgroundColor: "defaultBackground", top: 30, padding: 0,
            borderRadius: 4, borderWidth: 1, borderShadow: true, borderColor: "strokeSecondary"
        });
    }

    public async newDriverEventRow(props?) {
        const sourceDriverEvent: DataSource = new DataSource({ id: "sourceDriverEvent", url: "lme/dispatch/driver-events" });
        const sourceAutoHomeLocation: DataSource = new DataSource({ id: "sourceAutoHomeLocation", url: "lme/dispatch/auto-home-location" });
        sourceDriverEvent.layoutName = "lme/dispatch/DriverEvent";

        if (!props?.equipStatusId) {
            sourceDriverEvent.mode = DataSourceMode.ADD;
            const row = await sourceDriverEvent.createBlankRow();
            row.set({ ...props });
            sourceAutoHomeLocation.parentDataSource = sourceDriverEvent;
        } else {
            const response = await sourceDriverEvent.search({ id: props.equipStatusId });
            sourceDriverEvent.setRowsAndMode(DataSourceMode.UPDATE, response?.modelRows as ModelRow[]);
        }

        this.mainDataSource = sourceDriverEvent;
        this.mainDataSource.displayDataInBoundComponents();
    }

    private setupLayout() {
        this.driverEventLayout.panelDriverEventLocation.rowBreak = true;

        this.driverEventLayout.panelDates.borderLeftWidth = 0;
        this.driverEventLayout.panelDates.borderLeftColor = "transparent";
        this.driverEventLayout.panelDates.rowBreak = true;
        this.driverEventLayout.panelDates.paddingLeft = 0;

        this.driverEventLayout.panelStatusPriorityManagerRequest.borderLeftWidth = 0;
        this.driverEventLayout.panelStatusPriorityManagerRequest.borderLeftColor = "transparent";
        this.driverEventLayout.panelStatusPriorityManagerRequest.rowBreak = true;
        this.driverEventLayout.panelStatusPriorityManagerRequest.paddingLeft = 0;

        this.driverEventLayout.switchCreateAutoHome.visible = getDispatchControlBoolean("enable_auto_home");
    }

    public showSlideout(props: DriverEventProps) {
        this.driverEventLayout = Layout.getLayout("lme/dispatch/DriverEvent") as DriverEvent;
        this.newDriverEventRow(props)
        .then(() => {
            this.showDriverEventSlideout(props, this.mainDataSource.activeRow);
        })
        .catch(error => {
            const message = error.messages?.[0];
            Snackbar.showWarningSnackbar("Error loading Driver Event Slideout - " + message);
        });
    }

    showDriverEventSlideout(props: DriverEventProps, driverEventData?: ModelRow) {
        const layout = this.driverEventLayout;
        let saveButton: MultiButton;        
        const decorator = new EditRowDecorator({
            data: driverEventData,
            layout: layout,
            width: window.innerWidth * .33,
            title: props.equipStatusId ? "Driver Event - " + props.equipmentName : "Add Driver Event",
            fillVerticalSpace: true,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },

            layoutLoadListeners: async () => {
                layout.mainDataSource.mode = props?.equipStatusId ? DataSourceMode.UPDATE : DataSourceMode.ADD;
                layout.mainDataSource.rowIndex = 0;
                this.setupLayout();
            },

            doAfterSlideIn: async (decorator: EditRowDecorator) => {
                saveButton = decorator.multiButton;
                const data = layout.mainDataSource;
                layout.notificationDays = data.activeRow?.get("equipstatuscode.msg_warning_days");

                data.activeRow?.set("equipment_type_id", "D");
                if (data.mode === DataSourceMode.UPDATE) {
                    layout.sourceAutoHomeLocation.parentDataSource = layout.mainDataSource;
                    const response = await layout.sourceAutoHomeLocation.search({ "equipStatus_id": data.activeRow?.get("id") });
                    if (response.modelRows?.length > 0) {
                        layout.mainDataSource.activeRow?.addLinkedModel({ model: layout.sourceAutoHomeLocation.url, rows: [layout.sourceAutoHomeLocation.data[0]] });
                    }
                }

                const equipName = props.equipmentName;
                const equipId = props.equipmentId;
                if (equipId != null) {
                    data.activeRow?.set("equipment_id", equipId);
                    data.activeRow?.setLookupModelData("equipment_id", {id: equipId, first_name: equipName});

                    layout.textboxEquipmentId.displayData(layout.mainDataSource.activeRow, data?.activeRow?.getLookupModelData[0]?._lookup_equipment_id, 0);
                    layout.textboxEquipmentId.text = layout.textboxEquipmentId.text.trim();
                    if (!props.equipmentEditable) {
                        layout.textboxEquipmentId.enabled = false;
                    }
                }
            },
            onClose: () => {
                if (this.preventClose) {
                    this.preventClose = false;
                    return;
                }
                decorator.slideOut();
            },
            onSave: async () => {
                this.preventClose = false;
                saveButton.busy = true;
                this.doBeforeSave(layout);
                
                const inactivateResult = await layout.inactivateAutoHomeLocationOnStatusChange();
                if (inactivateResult === false) {
                    saveButton.busy = false;
                    this.preventClose = true;
                    return;

                }
                await layout.activeRow.post().then(async (row: ModelRow) => {
                    if (row && layout.switchCreateAutoHome.checked === true) {
                        layout.sourceAutoHomeLocation.activeRow.set("driver_id", row.data["equipment_id"]);
                        layout.sourceAutoHomeLocation.activeRow.set("equipstatus_id", row.data["id"]);
                        await this.postAutoHomeLocationForLayout(layout);
                    }
                    
                }).finally(() => {
                    saveButton.busy = false;
                });
                if (props.doAfterSave) {
                    props.doAfterSave();
                }
                saveButton.busy = false;
            }
        });
    }

    private doBeforeSave(layout: DriverEvent) {
        if (!layout.mainDataSource.activeRow.get("priority")) {
            layout.mainDataSource.activeRow.set("priority", 1);
        }
    }

    private async postAutoHomeLocationForLayout(layout: DriverEvent): Promise<void> {
        try {
            await layout.sourceAutoHomeLocation.activeRow.post();
        } catch (e) {
            CommonDialogs.showError(e);
        }
    }
}
